import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCreditCard, FiSmartphone, FiGlobe, FiShield, FiCheck, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { initiateRazorpayPayment, validatePaymentDetails } from '../services/razorpay';

const PaymentProcessor = ({ timeLeft, onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    paymentMethod: 'upi'
  });
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validatePaymentDetails(formData.email, formData.name, formData.phone);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsProcessing(true);
    setPaymentStatus('');

    try {
      const options = {
        amount: 9700, // ₹97 in paise
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          customer_name: formData.name,
          customer_email: formData.email,
          customer_phone: formData.phone,
          payment_method: formData.paymentMethod
        }
      };

      const result = await initiateRazorpayPayment(options);
      if (result.success) {
        setPaymentStatus('success');
        const paymentData = {
          paymentId: result.paymentId,
          customer: formData,
          timestamp: new Date().toISOString(),
          amount: 9700,
          product: 'diwali-ebook'
        };
        localStorage.setItem('lastPayment', JSON.stringify(paymentData));
        console.log('Payment successful:', paymentData);
        setTimeout(() => {
          onSuccess?.(formData);
        }, 2000);
      } else {
        setPaymentStatus('failed');
        setErrors({ general: result.error || 'Payment failed. Please try again.' });
      }
    } catch (error) {
      setPaymentStatus('failed');
      setErrors({ general: 'Something went wrong. Please try again.' });
      console.error('Payment error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (paymentStatus === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-3xl p-12 text-center"
      >
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Payment Successful! 🎉</h3>
        <p className="text-gray-600 mb-6">
          Thank you for your purchase! Redirecting you to the next step...
        </p>
        <div className="space-y-2 text-sm text-gray-500">
          <p>✅ 24 Ways to Double Your Sales</p>
          <p>✅ All 5 Bonuses Worth ₹25,495</p>
          <p>✅ Lifetime Access</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-8 text-gray-800"
    >
      <h3 className="text-2xl font-bold mb-6 text-center">Secure Checkout</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full p-4 border rounded-xl focus:outline-none focus:ring-2 ${
              errors.email
                ? 'border-red-500'
                : 'border-gray-300 focus:ring-purple-500'
            }`}
            disabled={isProcessing}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1 flex items-center">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1" />
              {errors.email}
            </p>
          )}
        </div>

        <div>
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full p-4 border rounded-xl focus:outline-none focus:ring-2 ${
              errors.name
                ? 'border-red-500'
                : 'border-gray-300 focus:ring-purple-500'
            }`}
            disabled={isProcessing}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1 flex items-center">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1" />
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number (10 digits)"
            value={formData.phone}
            onChange={handleInputChange}
            className={`w-full p-4 border rounded-xl focus:outline-none focus:ring-2 ${
              errors.phone
                ? 'border-red-500'
                : 'border-gray-300 focus:ring-purple-500'
            }`}
            disabled={isProcessing}
          />
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1 flex items-center">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1" />
              {errors.phone}
            </p>
          )}
        </div>

        <div>
          <h4 className="font-semibold mb-4">Choose Payment Method:</h4>
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'upi' }))}
              className={`p-3 border-2 rounded-xl flex items-center justify-center transition-colors ${
                formData.paymentMethod === 'upi'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-purple-200 hover:border-purple-500'
              }`}
              disabled={isProcessing}
            >
              <SafeIcon icon={FiSmartphone} className="w-6 h-6 mr-2" />
              <span className="text-sm">UPI</span>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'card' }))}
              className={`p-3 border-2 rounded-xl flex items-center justify-center transition-colors ${
                formData.paymentMethod === 'card'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-purple-200 hover:border-purple-500'
              }`}
              disabled={isProcessing}
            >
              <SafeIcon icon={FiCreditCard} className="w-6 h-6 mr-2" />
              <span className="text-sm">Cards</span>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'netbanking' }))}
              className={`p-3 border-2 rounded-xl flex items-center justify-center transition-colors ${
                formData.paymentMethod === 'netbanking'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-purple-200 hover:border-purple-500'
              }`}
              disabled={isProcessing}
            >
              <SafeIcon icon={FiGlobe} className="w-6 h-6 mr-2" />
              <span className="text-sm">Net Banking</span>
            </button>
          </div>
        </div>

        {errors.general && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
            {errors.general}
          </div>
        )}

        <button
          type="submit"
          disabled={isProcessing}
          className="w-full glow-button text-white py-4 rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Processing Payment...
            </span>
          ) : (
            <span>💥 Complete Payment - ₹97</span>
          )}
        </button>
      </form>

      <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 mt-4">
        <SafeIcon icon={FiShield} className="w-4 h-4 text-green-500" />
        <span>7-Day Action Guarantee – Try 5 tactics or get assistance</span>
      </div>

      <div className="mt-6 p-4 bg-green-50 rounded-xl">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-green-700">✅ You will receive:</span>
          <span className="text-gray-600">Instant Download</span>
        </div>
        <div className="mt-2 space-y-1 text-xs text-gray-600">
          <p>• 24 Ways to Double Your Sales (₹997)</p>
          <p>• Diwali Workshop (₹4,999)</p>
          <p>• All 5 Bonuses (₹25,495 Total)</p>
        </div>
      </div>
    </motion.div>
  );
};

export default PaymentProcessor;